import { useState, FormEvent } from "react";
import { reviewStore } from "@/utils/reviewStore";

interface Props {
  accommodationId: number;
  accommodationName: string;
  onClose: () => void;
  onSubmitted: () => void;
}

export default function ReviewWriteModal({ accommodationId, accommodationName, onClose, onSubmitted }: Props) {
  const [nickname, setNickname] = useState("");
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [text, setText] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<{ nickname?: string; text?: string }>({});

  const validate = () => {
    const e: typeof errors = {};
    if (!nickname.trim()) e.nickname = "닉네임을 입력해주세요.";
    else if (nickname.trim().length < 2) e.nickname = "닉네임은 2자 이상 입력해주세요.";
    if (!text.trim()) e.text = "리뷰 내용을 입력해주세요.";
    else if (text.trim().length < 10) e.text = "최소 10자 이상 작성해주세요.";
    return e;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length > 0) { setErrors(e2); return; }

    const now = new Date();
    const dateStr = `${now.getFullYear()}.${String(now.getMonth() + 1).padStart(2, "0")}`;

    reviewStore.add({
      accommodationId,
      accommodationName,
      nickname: nickname.trim(),
      rating,
      text: text.trim(),
      date: dateStr,
    });

    setSubmitted(true);
    setTimeout(() => {
      onSubmitted();
      onClose();
    }, 2200);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 px-0 sm:px-4" onClick={onClose}>
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {submitted ? (
          <div className="p-10 flex flex-col items-center text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mb-4">
              <i className="ri-check-double-line text-emerald-500 text-2xl"></i>
            </div>
            <h3 className="text-lg font-bold text-stone-800 mb-2">리뷰가 제출됐어요!</h3>
            <p className="text-sm text-stone-500 leading-relaxed">
              호스트 검토 후 게시될 예정이에요.<br />
              소중한 후기 감사합니다 :)
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between px-5 sm:px-6 py-4 sm:py-5 border-b border-stone-100 sticky top-0 bg-white z-10">
              <div>
                <h3 className="text-base font-bold text-stone-800">리뷰 작성</h3>
                <p className="text-xs text-stone-400 mt-0.5 truncate max-w-[220px] sm:max-w-xs">{accommodationName}</p>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 text-stone-400 cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="px-5 sm:px-6 py-4 sm:py-5 space-y-4 sm:space-y-5">
              {/* 닉네임 */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">
                  닉네임 <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={nickname}
                  onChange={(e) => { setNickname(e.target.value); setErrors((p) => ({ ...p, nickname: undefined })); }}
                  placeholder="표시될 이름을 입력하세요 (예: 김OO)"
                  className={`w-full px-3.5 py-2.5 text-sm border rounded-xl outline-none transition-all bg-stone-50 ${errors.nickname ? "border-red-300 focus:border-red-400" : "border-stone-200 focus:border-stone-400"}`}
                  maxLength={20}
                />
                {errors.nickname && <p className="text-xs text-red-500 mt-1">{errors.nickname}</p>}
              </div>

              {/* 별점 */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">별점</label>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="w-8 h-8 flex items-center justify-center cursor-pointer transition-transform hover:scale-110"
                    >
                      <i className={`${(hoverRating || rating) >= star ? "ri-star-fill text-amber-400" : "ri-star-line text-stone-300"} text-xl`}></i>
                    </button>
                  ))}
                  <span className="ml-2 text-sm font-semibold text-stone-600">
                    {["", "별로예요", "그저 그래요", "보통이에요", "좋았어요", "최고예요!"][hoverRating || rating]}
                  </span>
                </div>
              </div>

              {/* 리뷰 내용 */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">
                  리뷰 내용 <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={text}
                  onChange={(e) => { setText(e.target.value); setErrors((p) => ({ ...p, text: undefined })); }}
                  placeholder="숙소 이용 경험을 자유롭게 작성해주세요. (최소 10자)"
                  rows={4}
                  maxLength={500}
                  className={`w-full px-3.5 py-2.5 text-sm border rounded-xl outline-none transition-all resize-none bg-stone-50 ${errors.text ? "border-red-300 focus:border-red-400" : "border-stone-200 focus:border-stone-400"}`}
                />
                <div className="flex items-center justify-between mt-1">
                  {errors.text ? <p className="text-xs text-red-500">{errors.text}</p> : <span />}
                  <span className="text-xs text-stone-400">{text.length}/500</span>
                </div>
              </div>

              {/* 안내 문구 */}
              <div className="bg-amber-50 rounded-xl px-4 py-3 flex items-start gap-2">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                  <i className="ri-information-line text-amber-600 text-sm"></i>
                </div>
                <p className="text-xs text-amber-700 leading-relaxed">
                  리뷰는 호스트 검토 후 게시됩니다. 허위 내용이나 비방 글은 게시가 거부될 수 있어요.
                </p>
              </div>

              {/* 버튼 */}
              <div className="flex gap-2 pt-1 pb-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-3 border border-stone-200 text-stone-500 text-sm font-medium rounded-xl cursor-pointer whitespace-nowrap hover:bg-stone-50 transition-colors"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 bg-amber-500 text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap hover:bg-amber-600 transition-colors"
                >
                  리뷰 제출하기
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
